import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  Autocomplete,
  Box,
  IconButton,
  Paper,
  Chip,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  AddCircle as AddCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_ENDPOINTS, ADD_ENTRY_STATUS_OPTIONS, DAILY_INVENTORY_STATUS_COLORS } from '../../config';
import { 
  PRODUCTS, 
  GRADES, 
  BRANDS, 
  PACK_SIZES,
  loadDropdownOptions,
  addProductToDatabase,
  addGradeToDatabase,
  addBrandToDatabase,
  addPackSizeToDatabase,
  formatPackSizeDisplay,
  formatPackSizeStorage 
} from '../../utils/DROPDOWN_OPTIONS';
import LabelPrintDialog from '../dialogs/LabelPrintDialog';

const PACK_STYLE_OPTIONS = ['FINAL', 'FBB', 'PBB', 'FINAL TRAY'];

// PBB / PTB Pack Size Options (stored with "/" format)
const PBB_PACK_SIZES = ['1/10 KG', 'LOOSE KG'];

/* Styles that require default product "CISTSR" */
const TRAY_STYLES = ['FINAL TRAY'];

/* CS field format: Number + single Capital Letter + Number + '-' + Number
   Example: 1B1-1
   Regex enforces:
     ^[0-9]+[A-Z][0-9]+-[0-9]+$
*/
const CS_PATTERN = /^[0-9]+[A-Z][0-9]+-[0-9]+$/;

/* Product ID format: NNNNN-N-A-NN (5 digits - 1 digit - 1 letter - 2 digits)
   Example: 12345-1-A-01
   Regex enforces:
     ^[0-9]{5}-[0-9]-[A-Z]-[0-9]{2}$
*/
const PRODUCT_ID_PATTERN = /^[0-9]{5}-[0-9]-[A-Z]-[0-9]{2}$/;

// ✅ AUTO-ASSIGNED BRAND MAPPINGS (14 specific items - auto-fills brand, user can still change)
// These specific Product + Grade + Pack Size combinations auto-assign a Brand
const AUTO_LOCKED_BRAND_MAPPINGS = {
  // CPTO-NP
  'CPTO-NP|31/40|20/1 LB': 'PLAIN-HARRIS TEETER',
  
  // RPND
  'RPND|71/90|1/22 LB': 'TC',
  
  // RPTO
  'RPTO|26/30|6/5 LB': 'TC BLOOMIN',
  'RPTO|16/20|6/5 LB': 'TC BLOOMIN',
  
  // CEZP
  'CEZP|31/40|5/4 LB': 'TC GOLD STAR',
  
  // CIST EZP CUT
  'CIST EZP CUT|26/30|4/5 LB': 'TC HARRIS TEETER',
  'CIST EZP CUT|31/40|4/5 LB': 'TC HARRIS TEETER',
  'CIST EZP CUT|31/40|3/7 LB': 'TC HEB',
  
  // CISTSR
  'CISTSR|31/40|12/16 OZ': 'TC HEB',
  
  // CIST EZP CUT-TG
  'CIST EZP CUT-TG|16/20|5/2 LB': 'TC HEB',
  'CIST EZP CUT-TG|21/25|5/2 LB': 'TC HEB',
  'CIST EZP CUT-TG|13/15|5/2 LB': 'TC HEB',
  
  // REZPNC-TG
  'REZPNC-TG|6/8|5/2 LB': 'TC HEB',
  'REZPNC-TG|16/20|5/2 LB': 'TC HEB',
};

// ✅ NAV ID MASTER DATA - For filtering Pack Sizes and Brands
// Structure: Product -> Grade -> [{ pack_size, brand }]
const NAV_ID_MASTER_DATA = {
  // CBKN
  'CBKN': {
    '20/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '40/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'BROKEN': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'FALLEN PIE': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // CEZPNC
  'CEZPNC': {
    '31/40': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '12/12 OZ', brand: 'NATURES PROMISE' },
    ],
  },
  // CEZP
  'CEZP': {
    '31/40': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/2 LB', brand: 'GOLD STAR' },
      { pack_size: '4/4 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/4 LB', brand: 'TC GOLD STAR' },
    ],
  },
  // CIST EZP CUT-TG
  'CIST EZP CUT-TG': {
    '13/15': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'BOWL AND BASKET' },
    ],
    '16/20': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/1 LB', brand: 'SPECIALLY SELECTED' },
      { pack_size: '10/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'TC HEB' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '9/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'TC HEB' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
    ],
  },
  // CIST EZP CUT
  'CIST EZP CUT': {
    '16/20': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'NATURES PROMISE' },
      { pack_size: '14/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '4/4 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '4/5 LB', brand: 'TC HARRIS TEETER' },
      { pack_size: '5/2 LB', brand: 'HARRIS TEETER  FM' },
      { pack_size: '6/5 LB', brand: 'FINAL BAG BULK' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'BLACK TAIL' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '3/7 LB', brand: 'TC HEB' },
      { pack_size: '4/5 LB', brand: 'TC HARRIS TEETER' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
    ],
    '41/50': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '12/12 OZ', brand: 'NATURES PROMISE' },
    ],
  },
  // CISTO
  'CISTO': {
    '50/70': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // CISTSR-CM
  'CISTSR-CM': {
    '51/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/16 OZ', brand: 'BOWL AND BASKET' },
      { pack_size: '14/12 OZ', brand: 'PLAIN BAG BULK' },
      { pack_size: '16 OZ', brand: 'BOWL AND BASKET' },
    ],
  },
  // CISTSR
  'CISTSR': {
    '31/40': [
      { pack_size: '12/16 OZ', brand: 'TC HEB' },
      { pack_size: '16 OZ', brand: 'TC HEB' },
    ],
    '51/60': [
      { pack_size: '10/16 OZ', brand: 'FREMONT FISH MARKET' },
      { pack_size: '12 OZ', brand: 'TRAY BULK' },
      { pack_size: '12/12 OZ', brand: 'PLAIN BAG BULK' },
      { pack_size: '16 OZ', brand: 'FREMONT FISH MARKET' },
    ],
  },
  // CIST
  'CIST': {
    '21/25': [
      { pack_size: '1/10 KG', brand: 'REJECTION' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '16/12 OZ', brand: 'FREMONT FISH MARKET' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '16/12 OZ', brand: 'FREMONT FISH MARKET' },
      { pack_size: '20/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '24/12 OZ', brand: 'FINAL BAG BULK' },
    ],
    '41/60': [{ pack_size: '25/12 OZ', brand: 'PLAIN BAG BULK' }],
    '51/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'REJECTION' }],
  },
  // CPND-BKN
  'CPND-BKN': {
    '20/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '40/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // CPND-NP
  'CPND-NP': {
    '41/50': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'HARRIS TEETER  FM' },
    ],
    '71/90': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'HARRIS TEETER  FM' },
    ],
  },
  // CPND
  'CPND': {
    '100/150': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'GREAT CATCH' },
      { pack_size: '20/12 OZ', brand: 'PLAIN BAG BULK' },
      { pack_size: '24/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '25/12 OZ', brand: 'PLAIN BAG BULK' },
    ],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '71/90': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/16 OZ', brand: 'GREAT CATCH' },
    ],
    '91/110': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // CPTO-NP
  'CPTO-NP': {
    '31/40': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '12/12 OZ', brand: 'HARRIS TEETER  FM' },
      { pack_size: '20/1 LB', brand: 'PLAIN-HARRIS TEETER' },
    ],
    '41/50': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '51/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'HARRIS TEETER  FM' },
    ],
  },
  // CPTO
  'CPTO': {
    '16/20': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'SOUTHERN SEAS' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'BOWL AND BASKET' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/1 LB', brand: 'GOLD STAR' },
      { pack_size: '10/16 OZ', brand: 'GREAT CATCH' },
      { pack_size: '10/2 LB', brand: 'GREAT CATCH' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '18/16 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'MARKET BASKET' },
      { pack_size: '20/16 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'GOLD STAR' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/1 LB', brand: 'GOLD STAR' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '12/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '15/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '16/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '25/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '8/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '9/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '41/50': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/1 LB', brand: 'SOUTHERN SEAS' },
      { pack_size: '10/16 OZ', brand: 'GREAT CATCH' },
      { pack_size: '10/2 LB', brand: 'MARKET BASKET' },
      { pack_size: '20/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '27/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '30/1 LB', brand: 'PLAIN BAG BULK' },
    ],
    '41/60': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/12 OZ', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'GREAT CATCH' },
      { pack_size: '20/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '20/16 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '24/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '25/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '25/340 G', brand: 'PLAIN BAG BULK' },
      { pack_size: '35/12 OZ', brand: 'PLAIN BAG BULK' },
    ],
    '51/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'GOLD STAR' },
    ],
    '61/70': [{ pack_size: '1/10 KG', brand: 'REJECTION' }],
    '71/90': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '16/12 OZ', brand: 'SEAQUEEN' },
      { pack_size: '24/12 OZ', brand: 'FINAL BAG BULK' },
    ],
  },
  // HLSO-BT
  'HLSO-BT': {
    '13/15': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '16/20': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '21/25': [{ pack_size: '1/10 KG', brand: 'BLACK TAIL' }],
    '26/30': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '31/40': [{ pack_size: '1/10 KG', brand: 'BLACK TAIL' }],
    '41/50': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '6/8': [{ pack_size: '1/10 KG', brand: 'BLACK TAIL' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // HLSO
  'HLSO': {
    '100/150': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '100/200': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '31/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '8/12': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RBKN
  'RBKN': {
    '20/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '40/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'BROKEN': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RDCS
  'RDCS': {
    'DE-COLOUR': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // REZP-TG
  'REZP-TG': {
    '6/8': [
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '9/2 LB', brand: 'FINAL BAG BULK' },
    ],
  },
  // REZPNC-TG
  'REZPNC-TG': {
    '13/15': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '16/20': [{ pack_size: '5/2 LB', brand: 'TC HEB' }],
    '26/30': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '41/50': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '6/8': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '5/2 LB', brand: 'TC HEB' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '8/12': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/1 LB', brand: 'CHOICE HARVEST' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
    ],
  },
  // REZPNC
  'REZPNC': {
    '41/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '16/12 OZ', brand: 'FREMONT FISH MARKET' },
      { pack_size: '20/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '24/12 OZ', brand: 'FINAL BAG BULK' },
    ],
  },
  // REZP
  'REZP': {
    '13/15': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/32 OZ', brand: 'GREAT CATCH' },
    ],
    '16/20': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/2 LB', brand: 'GOLD STAR' },
    ],
    '16/25': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'GREAT CATCH' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '12/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '7/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'GOLD STAR' },
      { pack_size: '20/1 LB', brand: 'MARKET BASKET' },
      { pack_size: '5/2 LB', brand: 'SOUTHERN SEAS' },
      { pack_size: '7/2 LB', brand: 'PLAIN BAG BULK' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '16/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '20/1 LB', brand: 'GOLD STAR' },
      { pack_size: '3/2 LBS', brand: 'FINAL BAG BULK' },
      { pack_size: '3/9 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '7/2 LB', brand: 'PLAIN BAG BULK' },
    ],
    '41/50': [
      { pack_size: '10/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '14/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '6/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '7/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
    ],
    '41/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '20/12 OZ', brand: 'FINAL BAG BULK' },
      { pack_size: '24/12 OZ', brand: 'GREAT CATCH' },
    ],
    '51/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '12/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '18/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'BOWL AND BASKET' },
    ],
    '8/12': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RHLW
  'RHLW': {
    '61/70': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '71/90': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '91/110': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RPND-BKN
  'RPND-BKN': {
    '40/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    'ALL MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RPND-NP
  'RPND-NP': {
    '51/60': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'HARRIS TEETER  FM' },
      { pack_size: '20/12 OZ', brand: 'FINAL BAG BULK' },
    ],
  },
  // RPND
  'RPND': {
    '100/150': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '100/200': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '13/15': [{ pack_size: '1/10 KG', brand: 'DE COLOUR' }],
    '20/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '5/2 LB', brand: 'SOUTHERN SEAS' },
    ],
    '31/40': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '40/60': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '41/50': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '60/80': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
    '61/70': [{ pack_size: '10/1 LB', brand: 'PLAIN BAG BULK' }],
    '71/90': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '1/10.5 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '1/22 LB', brand: 'TC' },
      { pack_size: '12/1 LB', brand: 'PLAIN BAG BULK' },
    ],
    '91/110': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/1 LB', brand: 'PLAIN BAG BULK' },
    ],
  },
  // RPTO
  'RPTO': {
    '16/20': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '6/5 LB', brand: 'TC BLOOMIN' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
    ],
    '26/30': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '4/5 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '6/5 LB', brand: 'TC BLOOMIN' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '20/1 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '8/2 LB', brand: 'FINAL BAG BULK' },
    ],
    'MIX': [{ pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' }],
  },
  // RPVPD
  'RPVPD': {
    '31/40': [{ pack_size: '20/1 KG', brand: 'FINAL BAG BULK' }],
  },
  // RTNC-TG
  'RTNC-TG': {
    '13/15': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'BOWL AND BASKET' },
      { pack_size: '9/2 LB', brand: 'FINAL BAG BULK' },
    ],
  },
  // RTNC
  'RTNC': {
    '16/20': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '14/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
    ],
    '21/25': [
      { pack_size: '1/10 KG', brand: 'SISTER SIZE' },
      { pack_size: '10/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '12/12 OZ', brand: 'NATURES PROMISE' },
      { pack_size: '12/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '14/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '15/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '16/1 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '2/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '6/2 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '7/2 LB', brand: 'PLAIN BAG BULK' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
    ],
    '31/40': [
      { pack_size: '1/10 KG', brand: 'PLAIN BAG BULK' },
      { pack_size: '10/2 LB', brand: 'FREMONT FISH MARKET' },
      { pack_size: '15/1 LB', brand: 'FINAL BAG BULK' },
      { pack_size: '20/1 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '5/2 LB', brand: 'WHOLESOME PANTRY' },
      { pack_size: '8/2 LB', brand: 'PLAIN BAG BULK' },
    ],
  },
};

// Helper function to get filtered pack sizes based on Product + Grade
const getFilteredPackSizes = (product, grade, allPackSizes) => {
  if (!product || !grade) return allPackSizes;
  
  const productData = NAV_ID_MASTER_DATA[product.toUpperCase()];
  if (!productData) return allPackSizes;
  
  const gradeData = productData[grade.toUpperCase()];
  if (!gradeData) return allPackSizes;
  
  // Get pack sizes from NAV ID master for this Product + Grade
  const masterPackSizes = gradeData.map(item => item.pack_size);
  
  // Combine master pack sizes with all pack sizes, removing duplicates
  // Master pack sizes come first, then others
  const combined = [...new Set([...masterPackSizes, ...allPackSizes])];
  return combined;
};

// Helper function to get filtered brands based on Product + Grade + Pack Size
const getFilteredBrands = (product, grade, packSize, allBrands) => {
  if (!product || !grade || !packSize) return allBrands;
  
  const productData = NAV_ID_MASTER_DATA[product.toUpperCase()];
  if (!productData) return allBrands;
  
  const gradeData = productData[grade.toUpperCase()];
  if (!gradeData) return allBrands;
  
  // Find matching brands for this pack size
  const matchingBrands = gradeData
    .filter(item => item.pack_size.toUpperCase() === packSize.toUpperCase())
    .map(item => item.brand);
  
  if (matchingBrands.length === 0) return allBrands;
  
  // Combine matching brands with all brands, removing duplicates
  // Matching brands come first, then others
  const combined = [...new Set([...matchingBrands, ...allBrands])];
  return combined;
};

// Get auth config for axios requests
const getAuthConfig = () => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    },
    withCredentials: true
  };
};

// Get status color styling
const getStatusStyle = (status) => {
  const colorConfig = DAILY_INVENTORY_STATUS_COLORS[status];
  if (colorConfig) {
    return {
      backgroundColor: colorConfig.background,
      color: colorConfig.color,
    };
  }
  return {};
};

function AddEntryModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [fieldErrors, setFieldErrors] = useState({
    cs1: [],
    cs2: [],
    product_id: false,
  });

  // Label Print Dialog state
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printLabelData, setPrintLabelData] = useState(null);
  const [savedEntryData, setSavedEntryData] = useState(null);
  
  // ✅ State for dynamic dropdowns
  const [productsOptions, setProductsOptions] = useState([...PRODUCTS]);
  const [gradesOptions, setGradesOptions] = useState([...GRADES]);
  const [brandsOptions, setBrandsOptions] = useState([...BRANDS]);
  const [packSizesOptions, setPackSizesOptions] = useState([...PACK_SIZES].filter(ps => ps !== '10/32 OZ')); // Remove 10/32 OZ
  
  // ✅ NEW: Track if brand was auto-selected
  const [brandAutoSelected, setBrandAutoSelected] = useState(false);
  
  // ✅ State for adding new options
  const [showAddProductDialog, setShowAddProductDialog] = useState(false);
  const [showAddGradeDialog, setShowAddGradeDialog] = useState(false);
  const [showAddBrandDialog, setShowAddBrandDialog] = useState(false);
  const [showAddPackSizeDialog, setShowAddPackSizeDialog] = useState(false);
  
  const [newProductValue, setNewProductValue] = useState('');
  const [newGradeValue, setNewGradeValue] = useState('');
  const [newBrandValue, setNewBrandValue] = useState('');
  const [newPackSizeValue, setNewPackSizeValue] = useState('');
  
  const [addingProduct, setAddingProduct] = useState(false);
  const [addingGrade, setAddingGrade] = useState(false);
  const [addingBrand, setAddingBrand] = useState(false);
  const [addingPackSize, setAddingPackSize] = useState(false);
  
  // ✅ NEW: State for recent entries (24-hour dropdown suggestions)
  const [recentProductIds, setRecentProductIds] = useState([]);
  const [recentTraceIds, setRecentTraceIds] = useState([]);
  const [recentDayCodes, setRecentDayCodes] = useState([]);
  
  // ✅ Helper functions for recent entries with 24-hour expiry
  const RECENT_ENTRIES_KEY = 'daily_inventory_recent_entries';
  const CUSTOM_PACK_SIZES_KEY = 'daily_inventory_custom_pack_sizes';
  const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  
  const loadRecentEntries = () => {
    try {
      const stored = localStorage.getItem(RECENT_ENTRIES_KEY);
      if (!stored) return { product_ids: [], trace_ids: [], day_codes: [] };
      
      const data = JSON.parse(stored);
      const now = Date.now();
      
      // Filter out entries older than 24 hours
      const filterValid = (entries) => {
        if (!Array.isArray(entries)) return [];
        return entries.filter(entry => (now - entry.timestamp) < TWENTY_FOUR_HOURS);
      };
      
      return {
        product_ids: filterValid(data.product_ids),
        trace_ids: filterValid(data.trace_ids),
        day_codes: filterValid(data.day_codes),
      };
    } catch (err) {
      console.error('Error loading recent entries:', err);
      return { product_ids: [], trace_ids: [], day_codes: [] };
    }
  };
  
  const saveRecentEntry = (type, value) => {
    if (!value || !value.trim()) return;
    
    try {
      const data = loadRecentEntries();
      const now = Date.now();
      const newEntry = { value: value.trim(), timestamp: now };
      
      // Get the correct array based on type
      let entries = [];
      if (type === 'product_id') entries = data.product_ids;
      else if (type === 'trace_id') entries = data.trace_ids;
      else if (type === 'day_code') entries = data.day_codes;
      
      // Check if value already exists, update timestamp if so
      const existingIndex = entries.findIndex(e => e.value === value.trim());
      if (existingIndex >= 0) {
        entries[existingIndex].timestamp = now;
      } else {
        entries.unshift(newEntry); // Add to beginning
      }
      
      // Keep only last 20 entries per type
      entries = entries.slice(0, 20);
      
      // Update the correct array
      if (type === 'product_id') data.product_ids = entries;
      else if (type === 'trace_id') data.trace_ids = entries;
      else if (type === 'day_code') data.day_codes = entries;
      
      localStorage.setItem(RECENT_ENTRIES_KEY, JSON.stringify(data));
    } catch (err) {
      console.error('Error saving recent entry:', err);
    }
  };
  
  // ✅ Load custom pack sizes from localStorage (persists permanently, no expiry)
  const loadCustomPackSizes = () => {
    try {
      const stored = localStorage.getItem(CUSTOM_PACK_SIZES_KEY);
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      console.error('Error loading custom pack sizes:', err);
      return [];
    }
  };
  
  // ✅ Save a new custom pack size to localStorage
  const saveCustomPackSize = (packSize) => {
    if (!packSize || !packSize.trim()) return;
    try {
      const existing = loadCustomPackSizes();
      const normalized = packSize.toUpperCase().trim();
      // Don't save blocked pack sizes
      if (['10/32 OZ', '10X32 OZ', '10/32OZ'].includes(normalized)) return;
      // Avoid duplicates (case-insensitive)
      if (!existing.some(ps => ps.toUpperCase() === normalized)) {
        existing.push(normalized);
        existing.sort();
        localStorage.setItem(CUSTOM_PACK_SIZES_KEY, JSON.stringify(existing));
        console.log(`✅ Custom pack size "${normalized}" saved to localStorage`);
      }
    } catch (err) {
      console.error('Error saving custom pack size:', err);
    }
  };
  
  // ✅ Load recent entries when modal opens
  useEffect(() => {
    if (open) {
      const recent = loadRecentEntries();
      setRecentProductIds(recent.product_ids.map(e => e.value));
      setRecentTraceIds(recent.trace_ids.map(e => e.value));
      setRecentDayCodes(recent.day_codes.map(e => e.value));
    }
  }, [open]);
  
  // ✅ Load dropdown options from database on mount + merge localStorage custom pack sizes
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const result = await loadDropdownOptions();
        if (result.products?.length > 0) {
          setProductsOptions([...result.products]);
        }
        if (result.grades?.length > 0) {
          setGradesOptions([...result.grades]);
        }
        if (result.brands?.length > 0) {
          setBrandsOptions([...result.brands]);
        }
        
        // ✅ Filter out blocked pack sizes from DB
        let dbPackSizes = [];
        if (result.pack_sizes?.length > 0) {
          dbPackSizes = result.pack_sizes.filter(ps => 
            ps !== '10/32 OZ' && ps !== '10X32 OZ' && ps !== '10/32OZ'
          );
        }
        
        // ✅ Merge with custom pack sizes from localStorage
        const customPackSizes = loadCustomPackSizes();
        const mergedSet = new Set([...dbPackSizes, ...customPackSizes]);
        // Remove blocked ones from merged set too
        mergedSet.delete('10/32 OZ');
        mergedSet.delete('10X32 OZ');
        mergedSet.delete('10/32OZ');
        const mergedPackSizes = [...mergedSet].sort();
        
        setPackSizesOptions(mergedPackSizes);
        
        if (customPackSizes.length > 0) {
          console.log(`✅ Merged ${customPackSizes.length} custom pack sizes from localStorage`);
        }
      } catch (err) {
        console.error('Failed to load dropdown options:', err);
      }
    };
    
    if (open) {
      loadOptions();
    }
  }, [open]);

  // Base form data (common fields)
  const [formData, setFormData] = useState({
    production_date: new Date().toISOString().split('T')[0],
    shift: 'DAY',
    status: 'PRODUCED',
    product_id: '',
    pack_style: 'FINAL',
    product: '',
    grade: '',
    pack_size: '',
    brand: '',
    trace_id: '',
    day_code: '',
    remarks: '',
  });

  // Multiple pallets array - each pallet has its own quantities and location
  const [pallets, setPallets] = useState([
    {
      id: 1,
      pallet_id: '',
      lot_no: 'CTC-', // ✅ Default prefix
      cs1: '',
      cs2: '',
      cartons: 0,
      loose_bags: 0,
      loose_kgs: 0,
      total_kgs: 0,
      total_pounds: 0,
    }
  ]);

  // ✅ Auto-select brand when Product + Grade + Pack Size changes
  // Brand is auto-filled for the 14 specific mappings AND NAV ID master matches
  // User can always change the brand if required
  useEffect(() => {
    const { product, grade, pack_size } = formData;
    
    if (product && grade && pack_size) {
      // Normalize values for lookup
      const normalizedProduct = product.toUpperCase().trim();
      const normalizedGrade = grade.toUpperCase().trim();
      const normalizedPackSize = pack_size.toUpperCase().trim().replace(/X/g, '/');
      
      // Create lookup key
      const lookupKey = `${normalizedProduct}|${normalizedGrade}|${normalizedPackSize}`;
      
      // Check if this combination has an auto-assigned brand (14 specific items)
      const lockedBrand = AUTO_LOCKED_BRAND_MAPPINGS[lookupKey];
      
      if (lockedBrand) {
        // Auto-fill the brand - user can still change it if required
        setFormData(prev => ({ ...prev, brand: lockedBrand }));
        setBrandAutoSelected(true);
        console.log(`✅ Auto-assigned brand: ${lockedBrand} for ${lookupKey}`);
      } else {
        // Check NAV ID master data for suggested brand (inline logic)
        let suggestedBrand = null;
        const productData = NAV_ID_MASTER_DATA[normalizedProduct];
        if (productData) {
          const gradeData = productData[normalizedGrade];
          if (gradeData) {
            const match = gradeData.find(item => 
              item.pack_size.toUpperCase() === normalizedPackSize
            );
            if (match) {
              suggestedBrand = match.brand;
            }
          }
        }
        
        if (suggestedBrand && !formData.brand) {
          // Suggest brand but don't lock it
          setFormData(prev => ({ ...prev, brand: suggestedBrand }));
          setBrandAutoSelected(true);
          console.log(`✅ Auto-suggested brand: ${suggestedBrand} for ${lookupKey}`);
        } else if (brandAutoSelected && !suggestedBrand) {
          // Clear the brand when no suggestion available
          setFormData(prev => ({ ...prev, brand: '' }));
          setBrandAutoSelected(false);
        }
      }
    } else {
      // Not all fields selected - clear auto-assigned brand
      if (brandAutoSelected) {
        setFormData(prev => ({ ...prev, brand: '' }));
        setBrandAutoSelected(false);
      }
    }
  }, [formData.product, formData.grade, formData.pack_size]);
  
  // ✅ Helper function to check if brand was auto-assigned from the 14 specific mappings
  // Note: Brand is still editable even when auto-assigned
  const isBrandLocked = () => {
    const { product, grade, pack_size } = formData;
    if (!product || !grade || !pack_size) return false;
    
    const normalizedProduct = product.toUpperCase().trim();
    const normalizedGrade = grade.toUpperCase().trim();
    const normalizedPackSize = pack_size.toUpperCase().trim().replace(/X/g, '/');
    const lookupKey = `${normalizedProduct}|${normalizedGrade}|${normalizedPackSize}`;
    
    return AUTO_LOCKED_BRAND_MAPPINGS.hasOwnProperty(lookupKey);
  };

  // Check if Print Label should be enabled (only for PBB pack style)
  const isPrintLabelEnabled = () => {
    return formData.pack_style === 'PBB';
  };

  // Determine when loose_bags should be disabled (PBB or PTB)
  const isLooseBagsDisabled = () => {
    return formData.pack_style === 'PBB' || formData.pack_style === 'PTB(PLAIN TRAY BULK)';
  };

  // Determine when KG Loose should be disabled: FINAL, FBB, or FTB
  const isKgLooseDisabled = () => {
    return (
      formData.pack_style === 'FINAL' ||
      formData.pack_style === 'FBB' ||
      formData.pack_style === 'FTB(FINAL TRAY BULK)' ||
      formData.pack_style === 'FINAL TRAY'
    );
  };

  // Determine if CS1 should be disabled
  // NOTE: PBB is now ENABLED for CS1 (removed from disabled condition)
  const isCS1Disabled = () => {
    return (
      formData.pack_size === 'LOOSE KG' ||
      formData.pack_size === '1/10 KG'
    );
  };

  // Get available pack sizes based on pack style
  const getAvailablePackSizes = () => {
    if (formData.pack_style === 'PTB(PLAIN TRAY BULK)') {
      return PBB_PACK_SIZES;
    }
    
    // ✅ PBB: Show PBB defaults first, then all other pack sizes
    if (formData.pack_style === 'PBB') {
      const otherPackSizes = packSizesOptions.filter(ps => 
        !PBB_PACK_SIZES.includes(ps) && ps !== '10/32 OZ' && ps !== '10X32 OZ' && ps !== '10/32OZ'
      );
      return [...PBB_PACK_SIZES, ...otherPackSizes];
    }
    
    // ✅ Filter pack sizes based on Product + Grade from NAV ID master (inline)
    let baseOptions = packSizesOptions;
    if (formData.product && formData.grade) {
      baseOptions = getFilteredPackSizes(formData.product, formData.grade, packSizesOptions);
    }
    
    // ✅ Filter out 10/32 OZ from all pack size lists
    return baseOptions.filter(ps => 
      ps !== '10/32 OZ' && ps !== '10X32 OZ' && ps !== '10/32OZ'
    );
  };
  
  // ✅ Get available brands based on Product + Grade + Pack Size (inline filtering)
  const getAvailableBrands = () => {
    if (formData.product && formData.grade && formData.pack_size) {
      return getFilteredBrands(formData.product, formData.grade, formData.pack_size, brandsOptions);
    }
    return brandsOptions;
  };

  // Clear loose_bags when it becomes disabled
  useEffect(() => {
    if (isLooseBagsDisabled()) {
      setPallets(prev => prev.map(p => ({ ...p, loose_bags: 0 })));
    }
  }, [formData.pack_style]);

  // Clear loose_kgs when KG loose becomes disabled
  useEffect(() => {
    if (isKgLooseDisabled()) {
      setPallets(prev => prev.map(p => ({ ...p, loose_kgs: 0 })));
    }
  }, [formData.pack_style]);

  // Auto-disable CS1 when pack_size is "LOOSE KG" or "1/10 KG" (PBB now allowed)
  useEffect(() => {
    const shouldDisableCS1 =
      formData.pack_size === 'LOOSE KG' ||
      formData.pack_size === '1/10 KG';

    if (shouldDisableCS1) {
      setPallets(prev => prev.map(p => ({ ...p, cs1: '' })));
      setFieldErrors(prev => ({ ...prev, cs1: prev.cs1.map(() => false) }));
    }
  }, [formData.pack_style, formData.pack_size]);

  // Clear pack_size when switching to PTB if incompatible
  useEffect(() => {
    if (formData.pack_style === 'PTB(PLAIN TRAY BULK)') {
      if (formData.pack_size && !PBB_PACK_SIZES.includes(formData.pack_size)) {
        setFormData(prev => ({ ...prev, pack_size: '' }));
      }
    }
  }, [formData.pack_style]);

  // set default product to 'CISTSR' when pack_style is a tray style
  useEffect(() => {
    if (TRAY_STYLES.includes(formData.pack_style)) {
      if (formData.product !== 'CISTSR') {
        setFormData(prev => ({ ...prev, product: 'CISTSR' }));
      }
    }
  }, [formData.pack_style]);

  /**
   * Parse pack size to extract bags per carton and weight per bag
   * Accepts both "X" and "/" separators (e.g. "10X1 LB" or "10/1 LB")
   * ✅ Supports decimal values (e.g. "30/5.4 OZ", "12.5/2.75 LB")
   */
  const parsePackSize = (packSizeStr) => {
    if (!packSizeStr) return { bagsPerCarton: 0, weightPerBag: 0, unit: 'KG' };

    const packSizeUpper = packSizeStr.toUpperCase().trim();

    const match = packSizeUpper.match(/(\d+\.?\d*)\s*[Xx\/]\s*(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/);

    if (match) {
      const bagsPerCarton = parseFloat(match[1]) || 0;
      const weightPerBag = parseFloat(match[2]) || 0;
      const unit = (match[3] || 'KG').replace('LBS', 'LB').replace('KGS', 'KG');
      return { bagsPerCarton, weightPerBag, unit };
    }

    const numericMatch = packSizeUpper.match(/(\d+\.?\d*)\s*(OZ|LB|LBS|KG|KGS)?/);
    if (numericMatch) {
      const weightPerBag = parseFloat(numericMatch[1]) || 0;
      const unit = (numericMatch[2] || 'KG').replace('LBS', 'LB').replace('KGS', 'KG');
      return { bagsPerCarton: 1, weightPerBag, unit };
    }

    return { bagsPerCarton: 0, weightPerBag: 0, unit: 'KG' };
  };

  const convertToKg = (weight, unit) => {
    switch (unit) {
      case 'OZ':
        return weight * 0.0283495;
      case 'LB':
        return weight * 0.453592;
      case 'KG':
      default:
        return weight;
    }
  };

  // Memoized calculation of totals for display (doesn't update state)
  const palletsWithCalculatedTotals = React.useMemo(() => {
    return pallets.map(pallet => {
      const cartons = parseInt(pallet.cartons) || 0;
      const looseBags = parseInt(pallet.loose_bags) || 0;
      const looseKgs = parseFloat(pallet.loose_kgs) || 0;

      const { bagsPerCarton, weightPerBag, unit } = parsePackSize(formData.pack_size);

      let totalKgs = 0;

      if (formData.pack_style === 'FINAL' || formData.pack_style === 'FBB' || formData.pack_style === 'FINAL TRAY' || formData.pack_style === 'FTB(FINAL TRAY BULK)') {
        const totalBags = (bagsPerCarton * cartons) + looseBags;
        const totalWeight = totalBags * weightPerBag;
        totalKgs = convertToKg(totalWeight, unit);
      } else if (formData.pack_style === 'PBB' || formData.pack_style === 'PTB(PLAIN TRAY BULK)') {
        if (cartons === 0) {
          totalKgs = looseKgs;
        } else {
          const cartonsWeight = cartons * weightPerBag;
          const cartonsKg = convertToKg(cartonsWeight, unit);
          totalKgs = cartonsKg + looseKgs;
        }
      }

      const totalPounds = totalKgs * 2.2;

      return {
        ...pallet,
        total_kgs: totalKgs,
        total_pounds: totalPounds,
      };
    });
  }, [pallets, formData.pack_style, formData.pack_size]);

  const validateCS = (value) => {
    if (!value) return false;
    return CS_PATTERN.test(value);
  };

  // ✅ Validate Product ID format: NNNNN-N-A-NN
  const validateProductId = (value) => {
    if (!value) return false;
    return PRODUCT_ID_PATTERN.test(value);
  };

  // ✅ Sanitize Product ID input - format as user types
  const sanitizeProductIdInput = (raw) => {
    if (raw === undefined || raw === null) return '';
    // Remove any characters that aren't numbers, letters, or hyphens
    let cleaned = String(raw).replace(/[^0-9A-Za-z-]/g, '');
    // Convert letters to uppercase
    cleaned = cleaned.replace(/[a-z]/g, (c) => c.toUpperCase());
    return cleaned;
  };

  const sanitizeCSInput = (raw) => {
    if (raw === undefined || raw === null) return '';
    let cleaned = String(raw).replace(/[^0-9A-Za-z-]/g, '');
    cleaned = cleaned.replace(/[a-z]/g, (c) => c.toUpperCase());
    return cleaned;
  };

  const handleChange = (field, value) => {
    // ✅ If user manually changes brand, clear the auto-selected flag
    if (field === 'brand') {
      setBrandAutoSelected(false);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle pallet field change
  const handlePalletChange = (palletIndex, field, value) => {
    if (field === 'cs1' || field === 'cs2') {
      value = sanitizeCSInput(value);
    }
    
    setPallets(prev => {
      const updated = [...prev];
      updated[palletIndex] = {
        ...updated[palletIndex],
        [field]: value,
      };
      return updated;
    });
  };

  // Add new pallet
  const addPallet = () => {
    const newId = pallets.length > 0 ? Math.max(...pallets.map(p => p.id)) + 1 : 1;
    setPallets(prev => [
      ...prev,
      {
        id: newId,
        pallet_id: '',
        lot_no: 'CTC-', // ✅ Default prefix
        cs1: '',
        cs2: '',
        cartons: 0,
        loose_bags: 0,
        loose_kgs: 0,
        total_kgs: 0,
        total_pounds: 0,
      }
    ]);
    setFieldErrors(prev => ({
      ...prev,
      cs1: [...prev.cs1, false],
      cs2: [...prev.cs2, false],
    }));
  };

  // Remove pallet
  const removePallet = (palletIndex) => {
    if (pallets.length <= 1) {
      setError('❌ At least one pallet entry is required');
      return;
    }
    setPallets(prev => prev.filter((_, index) => index !== palletIndex));
    setFieldErrors(prev => ({
      cs1: prev.cs1.filter((_, index) => index !== palletIndex),
      cs2: prev.cs2.filter((_, index) => index !== palletIndex),
    }));
  };

  const handleBlurCS = (palletIndex, field) => {
    const val = pallets[palletIndex][field];
    if (field === 'cs1' && isCS1Disabled()) {
      setFieldErrors(prev => {
        const updated = [...prev.cs1];
        updated[palletIndex] = false;
        return { ...prev, cs1: updated };
      });
      return;
    }
    if (!val) {
      setFieldErrors(prev => {
        const updated = [...prev[field]];
        updated[palletIndex] = false;
        return { ...prev, [field]: updated };
      });
      return;
    }
    const ok = validateCS(val);
    setFieldErrors(prev => {
      const updated = [...prev[field]];
      updated[palletIndex] = !ok;
      return { ...prev, [field]: updated };
    });
  };

  // Open Print Label Dialog for a specific pallet
  const handleOpenPrintDialog = (palletIndex) => {
    const pallet = pallets[palletIndex];
    const calculatedPallet = palletsWithCalculatedTotals[palletIndex];
    
    if (!pallet.lot_no) {
      setError('❌ Please enter Lot No before printing labels');
      return;
    }
    
    if (!pallet.cartons || pallet.cartons < 1) {
      setError('❌ Please enter number of cartons before printing labels');
      return;
    }

    setPrintLabelData({
      product: formData.product,
      grade: formData.grade,
      pack_size: formData.pack_size,
      lot_no: pallet.lot_no,
      production_date: formData.production_date,
      cartons: pallet.cartons,
      inventory_id: null, // Will be set after save if needed
    });
    setPrintDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Product ID format validation
    if (!formData.product_id) {
      setError('❌ Product ID is required');
      return;
    }
    if (!validateProductId(formData.product_id)) {
      setFieldErrors(prev => ({ ...prev, product_id: true }));
      setError('❌ Product ID must match format: NNNNN-N-A-NN (e.g., 12345-1-A-01)');
      return;
    }

    // ✅ PACK STYLE SPECIFIC VALIDATIONS
    const isFinalStyle = formData.pack_style === 'FINAL';
    const isPBBStyle = formData.pack_style === 'PBB';

    // ✅ Required fields for ALL pack styles: Product, Grade, Pack Size, Brand
    if (!formData.product) {
      setError('❌ Product is required');
      return;
    }
    if (!formData.grade) {
      setError('❌ Grade is required');
      return;
    }
    if (!formData.pack_size) {
      setError('❌ Pack Size is required');
      return;
    }
    if (!formData.brand) {
      setError('❌ Brand is required');
      return;
    }

    // ✅ FINAL specific required fields: Frame ID (trace_id) and Day Code
    if (isFinalStyle) {
      if (!formData.trace_id) {
        setError('❌ Frame ID is required for FINAL pack style');
        return;
      }
      if (!formData.day_code) {
        setError('❌ Day Code is required for FINAL pack style');
        return;
      }
    }

    // Validate each pallet based on pack style
    for (let i = 0; i < pallets.length; i++) {
      const pallet = pallets[i];
      
      // Lot No required for both FINAL and PBB
      if (isFinalStyle || isPBBStyle) {
        if (!pallet.lot_no || pallet.lot_no === 'CTC-') {
          setError(`❌ Lot No is required for Pallet ${i + 1}`);
          return;
        }
      }

      // Pallet ID required for both FINAL and PBB
      if (isFinalStyle || isPBBStyle) {
        if (!pallet.pallet_id) {
          setError(`❌ Pallet ID is required for Pallet ${i + 1}`);
          return;
        }
      }

      // ✅ CARTONS validation - NOT mandatory (can have only loose bags or loose kgs)
      // Cartons field is optional - user can enter 0 cartons if they only have loose bags/kgs

      // ✅ FINAL pack style: Can be placed in CS1 OR CS2 (at least one required)
      if (isFinalStyle) {
        if (!pallet.cs1 && !pallet.cs2) {
          setError(`❌ At least one Bin Location (CS1 or CS2) is required for Pallet ${i + 1} (FINAL pack style)`);
          return;
        }
        // Validate CS1 format if provided
        if (pallet.cs1 && !validateCS(pallet.cs1)) {
          setFieldErrors(prev => {
            const updated = [...prev.cs1];
            updated[i] = true;
            return { ...prev, cs1: updated };
          });
          setError(`❌ CS1 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
          return;
        }
        // Validate CS2 format if provided
        if (pallet.cs2 && !validateCS(pallet.cs2)) {
          setFieldErrors(prev => {
            const updated = [...prev.cs2];
            updated[i] = true;
            return { ...prev, cs2: updated };
          });
          setError(`❌ CS2 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
          return;
        }
      }

      // ✅ PBB (BULK) pack style: CS2 required, CS1 now optional but allowed
      if (isPBBStyle) {
        if (!pallet.cs2) {
          setError(`❌ CS2 Bin Location is required for Pallet ${i + 1} (PBB/BULK pack style)`);
          return;
        }
        if (!validateCS(pallet.cs2)) {
          setFieldErrors(prev => {
            const updated = [...prev.cs2];
            updated[i] = true;
            return { ...prev, cs2: updated };
          });
          setError(`❌ CS2 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
          return;
        }
        // Validate CS1 format if provided (now optional for PBB)
        if (pallet.cs1 && !validateCS(pallet.cs1)) {
          setFieldErrors(prev => {
            const updated = [...prev.cs1];
            updated[i] = true;
            return { ...prev, cs1: updated };
          });
          setError(`❌ CS1 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
          return;
        }
      }

      // Optional CS validations for other pack styles (if provided but not required)
      if (pallet.cs1 && !isFinalStyle && !isCS1Disabled() && !validateCS(pallet.cs1)) {
        setFieldErrors(prev => {
          const updated = [...prev.cs1];
          updated[i] = true;
          return { ...prev, cs1: updated };
        });
        setError(`❌ CS1 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
        return;
      }
      if (pallet.cs2 && !isPBBStyle && !isFinalStyle && !validateCS(pallet.cs2)) {
        setFieldErrors(prev => {
          const updated = [...prev.cs2];
          updated[i] = true;
          return { ...prev, cs2: updated };
        });
        setError(`❌ CS2 in Pallet ${i + 1} must match pattern: Number + CAPITAL LETTER + Number - Number (e.g., 1B1-1)`);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare entries for API
      const entries = palletsWithCalculatedTotals.map(pallet => ({
        ...formData,
        pack_size: formatPackSizeStorage(formData.pack_size),
        pallet_id: pallet.pallet_id,
        lot_no: pallet.lot_no,
        cs1: pallet.cs1,
        cs2: pallet.cs2,
        cartons: pallet.cartons,
        loose_bags: pallet.loose_bags,
        loose_kgs: pallet.loose_kgs,
        total_kgs: pallet.total_kgs,
        total_pounds: pallet.total_pounds,
      }));

      const response = await axios.post(
        API_ENDPOINTS.INVENTORY,
        { entries },
        getAuthConfig()
      );

      if (response.data.success) {
        const count = response.data.count || entries.length;
        setSuccess(`✅ Successfully added ${count} entr${count > 1 ? 'ies' : 'y'}!`);
        setSavedEntryData(response.data);
        
        // ✅ Save recent entries for 24-hour dropdown
        if (formData.product_id) saveRecentEntry('product_id', formData.product_id);
        if (formData.trace_id) saveRecentEntry('trace_id', formData.trace_id);
        if (formData.day_code) saveRecentEntry('day_code', formData.day_code);
        
        // ✅ Refresh recent entries state
        const recent = loadRecentEntries();
        setRecentProductIds(recent.product_ids.map(e => e.value));
        setRecentTraceIds(recent.trace_ids.map(e => e.value));
        setRecentDayCodes(recent.day_codes.map(e => e.value));
        
        setTimeout(() => {
          onSuccess && onSuccess();
          handleClose();
        }, 1500);
      } else {
        setError(response.data.error || 'Failed to add entry');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.error || 'Failed to add entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      production_date: new Date().toISOString().split('T')[0],
      shift: 'DAY',
      status: 'PRODUCED',
      product_id: '',
      pack_style: 'FINAL',
      product: '',
      grade: '',
      pack_size: '',
      brand: '',
      trace_id: '',
      day_code: '',
      remarks: '',
    });
    setPallets([{
      id: 1,
      pallet_id: '',
      lot_no: 'CTC-', // ✅ Reset with default prefix
      cs1: '',
      cs2: '',
      cartons: 0,
      loose_bags: 0,
      loose_kgs: 0,
      total_kgs: 0,
      total_pounds: 0,
    }]);
    setFieldErrors({ cs1: [], cs2: [], product_id: false });
    setError('');
    setSuccess('');
    setBrandAutoSelected(false);
    onClose();
  };

  // ✅ Handle adding new product
  const handleAddProduct = async () => {
    if (!newProductValue.trim()) {
      setError('Please enter a product code');
      return;
    }
    
    setAddingProduct(true);
    setError('');
    
    try {
      const result = await addProductToDatabase(newProductValue);
      console.log('Add product result:', result);
      
      if (result.success) {
        const addedProduct = result.product || newProductValue.toUpperCase().trim();
        if (!productsOptions.includes(addedProduct)) {
          setProductsOptions(prev => [...prev, addedProduct].sort());
        }
        handleChange('product', addedProduct);
        setNewProductValue('');
        setShowAddProductDialog(false);
        setError('');
        
        const msg = result.exists 
          ? `Product "${addedProduct}" already exists - selected!`
          : result.message || `Product "${addedProduct}" added successfully!`;
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to add product. Please try again.');
        console.error('Add product error:', result);
      }
    } catch (err) {
      console.error('Add product exception:', err);
      setError('Failed to add product: ' + (err.message || 'Network error'));
    } finally {
      setAddingProduct(false);
    }
  };

  // ✅ Handle adding new grade
  const handleAddGrade = async () => {
    if (!newGradeValue.trim()) {
      setError('Please enter a grade value');
      return;
    }
    
    // Validate grade format
    const normalized = newGradeValue.replace(/[xX]/g, '/').toUpperCase().trim();
    
    setAddingGrade(true);
    setError('');
    
    try {
      const result = await addGradeToDatabase(newGradeValue);
      console.log('Add grade result:', result);
      
      if (result.success) {
        const addedGrade = result.grade || normalized;
        if (!gradesOptions.includes(addedGrade)) {
          setGradesOptions(prev => [...prev, addedGrade].sort());
        }
        handleChange('grade', addedGrade);
        setNewGradeValue('');
        setShowAddGradeDialog(false);
        setError('');
        
        const msg = result.exists 
          ? `Grade "${addedGrade}" already exists - selected!`
          : result.message || `Grade "${addedGrade}" added successfully!`;
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to add grade. Please try again.');
        console.error('Add grade error:', result);
      }
    } catch (err) {
      console.error('Add grade exception:', err);
      setError('Failed to add grade: ' + (err.message || 'Network error'));
    } finally {
      setAddingGrade(false);
    }
  };
  
  // ✅ Handle adding new pack size — SUPPORTS DECIMAL VALUES (e.g., 30/5.4 OZ, 12.5/2.75 LB)
  const handleAddPackSize = async () => {
    if (!newPackSizeValue.trim()) {
      setError('Please enter a pack size value');
      return;
    }
    
    // ✅ Validate format - SUPPORTS DECIMALS in both numerator and denominator
    const normalized = newPackSizeValue.replace(/[xX]/g, '/').toUpperCase().trim();
    
    // ✅ UPDATED Regex pattern - supports decimal values in BOTH numerator and denominator
    // Format: Number/Number UNIT where Number can be integer or decimal
    // Valid examples: 10/1 LB, 30/5.4 OZ, 12.5/2.75 LB, 1/10 KG, 0.5/2 LB
    const packSizePattern = /^\d+\.?\d*\/\d+\.?\d*\s+(LB|OZ|KG|LBS|KGS)$/i;
    
    if (!packSizePattern.test(normalized) && normalized !== 'LOOSE KG') {
      setError('Invalid format. Use: Number/Number UNIT\n\n✅ Standard: "10/1 LB", "12/16 OZ", "1/10 KG"\n✅ With decimals: "30/5.4 OZ", "12.5/2.75 LB", "0.5/2 KG"');
      return;
    }
    
    // ✅ Block adding 10/32 OZ variants
    const blocked1032Variants = ['10/32 OZ', '10X32 OZ', '10/32OZ'];
    if (blocked1032Variants.some(blocked => normalized === blocked)) {
      setError('❌ 10/32 OZ is not allowed. Please use a different pack size.');
      return;
    }
    
    setAddingPackSize(true);
    setError('');
    
    try {
      const result = await addPackSizeToDatabase(newPackSizeValue);
      console.log('✅ Add pack size result:', result);
      
      if (result.success) {
        const addedPackSize = result.pack_size || normalized;
        if (!packSizesOptions.includes(addedPackSize)) {
          setPackSizesOptions(prev => [...prev, addedPackSize].sort());
        }
        // ✅ Persist to localStorage so it appears in dropdown automatically next time
        saveCustomPackSize(addedPackSize);
        handleChange('pack_size', addedPackSize);
        setNewPackSizeValue('');
        setShowAddPackSizeDialog(false);
        setError('');
        
        const msg = result.exists 
          ? `Pack size "${addedPackSize}" already exists - selected!`
          : result.message || `✅ Pack size "${addedPackSize}" added successfully!`;
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to add pack size. Please try again.');
        console.error('❌ Add pack size error:', result);
      }
    } catch (err) {
      console.error('❌ Add pack size exception:', err);
      // ✅ Fallback: Save to localStorage even if DB fails, so it appears next time
      saveCustomPackSize(normalized);
      if (!packSizesOptions.includes(normalized)) {
        setPackSizesOptions(prev => [...prev, normalized].sort());
      }
      handleChange('pack_size', normalized);
      setNewPackSizeValue('');
      setShowAddPackSizeDialog(false);
      setSuccess(`✅ Pack size "${normalized}" saved locally (DB unavailable - will sync later)`);
      setTimeout(() => setSuccess(''), 4000);
    } finally {
      setAddingPackSize(false);
    }
  };

  // ✅ Handle adding new brand
  const handleAddBrand = async () => {
    if (!newBrandValue.trim()) {
      setError('Please enter a brand name');
      return;
    }
    
    setAddingBrand(true);
    setError('');
    
    try {
      const result = await addBrandToDatabase(newBrandValue);
      console.log('Add brand result:', result);
      
      if (result.success) {
        const addedBrand = result.brand || newBrandValue.toUpperCase().trim();
        if (!brandsOptions.includes(addedBrand)) {
          setBrandsOptions(prev => [...prev, addedBrand].sort());
        }
        handleChange('brand', addedBrand);
        setNewBrandValue('');
        setShowAddBrandDialog(false);
        setError('');
        
        const msg = result.exists 
          ? `Brand "${addedBrand}" already exists - selected!`
          : result.message || `Brand "${addedBrand}" added successfully!`;
        setSuccess(msg);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.error || 'Failed to add brand. Please try again.');
      }
    } catch (err) {
      console.error('Add brand exception:', err);
      setError('Failed to add brand: ' + (err.message || 'Network error'));
    } finally {
      setAddingBrand(false);
    }
  };

  // Calculate grand totals from calculated pallets
  const grandTotalKgs = palletsWithCalculatedTotals.reduce((sum, p) => sum + (p.total_kgs || 0), 0);
  const grandTotalPounds = palletsWithCalculatedTotals.reduce((sum, p) => sum + (p.total_pounds || 0), 0);
  const grandTotalCartons = palletsWithCalculatedTotals.reduce((sum, p) => sum + (parseInt(p.cartons) || 0), 0);

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white', fontWeight: 600 }}>
          Add New Inventory Entry
          {pallets.length > 1 && (
            <Chip 
              label={`${pallets.length} Pallets`} 
              size="small" 
              sx={{ ml: 2, bgcolor: 'rgba(255,255,255,.2)', color: 'white' }}
            />
          )}
          {isPrintLabelEnabled() && (
            <Chip 
              icon={<PrintIcon sx={{ fontSize: 16 }} />}
              label="Label Print Available" 
              size="small" 
              sx={{ ml: 1, bgcolor: '#4caf50', color: 'white' }}
            />
          )}
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ mt: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Alert severity="info" sx={{ mb: 3 }}>
              <strong>Note:</strong> NAV ID will be automatically assigned based on Product, Brand, Grade, and Pack Size combination after submission.
              <br />
              <strong>Multi-Pallet:</strong> You can add multiple pallets with different Lot No, Pallet ID, and Bin Locations for the same product.
              {isPrintLabelEnabled() && (
                <>
                  <br />
                  <strong>🖨️ Label Printing:</strong> PBB pack style selected - Print Labels button is available for each pallet.
                </>
              )}
              <br />
              <strong>✨ Smart Selection:</strong> Pack Size and Brand dropdowns show matching options from NAV ID master first.
              <br />
              <strong>✨ Auto-Assigned Brands:</strong> 14 specific Product + Grade + Pack Size combinations auto-fill the brand. You can still change it if required.
              <br />
              <strong>📋 Required Fields:</strong>{' '}
              {formData.pack_style === 'FINAL' && (
                <span style={{ color: '#1565c0' }}>
                  FINAL → Product, Grade, Pack Size, Brand, Trace ID, Day Code, Lot No, Pallet ID, CS1
                </span>
              )}
              {formData.pack_style === 'PBB' && (
                <span style={{ color: '#9c27b0' }}>
                  PBB → Product, Grade, Pack Size, Brand, Lot No, Pallet ID, CS2
                </span>
              )}
              {formData.pack_style !== 'FINAL' && formData.pack_style !== 'PBB' && (
                <span style={{ color: '#666' }}>
                  Varies by pack style
                </span>
              )}
            </Alert>

            {/* Basic Information */}
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Basic Information
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Production Date *"
                  value={formData.production_date}
                  onChange={(e) => handleChange('production_date', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Shift *"
                  value={formData.shift}
                  onChange={(e) => handleChange('shift', e.target.value)}
                >
                  <MenuItem value="DAY">DAY</MenuItem>
                  <MenuItem value="NIGHT">NIGHT</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Status *"
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  sx={getStatusStyle(formData.status)}
                >
                  {ADD_ENTRY_STATUS_OPTIONS.map((status) => (
                    <MenuItem 
                      key={status} 
                      value={status}
                      sx={getStatusStyle(status)}
                    >
                      {status}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <Autocomplete
                  freeSolo
                  options={recentProductIds}
                  value={formData.product_id}
                  onChange={(e, newValue) => {
                    const sanitized = sanitizeProductIdInput(newValue || '');
                    handleChange('product_id', sanitized);
                    if (fieldErrors.product_id) {
                      setFieldErrors(prev => ({ ...prev, product_id: false }));
                    }
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === 'input') {
                      const sanitized = sanitizeProductIdInput(newValue || '');
                      handleChange('product_id', sanitized);
                      if (fieldErrors.product_id) {
                        setFieldErrors(prev => ({ ...prev, product_id: false }));
                      }
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Recent" size="small" color="info" sx={{ height: 20, fontSize: '0.7rem' }} />
                        {option}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required
                      label="Product ID *"
                      error={fieldErrors.product_id}
                      placeholder="NNNNN-N-A-NN"
                      helperText={
                        recentProductIds.length > 0
                          ? (fieldErrors.product_id ? "Format: NNNNN-N-A-NN" : "Select recent or type new")
                          : "Format: NNNNN-N-A-NN (e.g., 12345-1-A-01)"
                      }
                      onBlur={() => {
                        if (formData.product_id && !validateProductId(formData.product_id)) {
                          setFieldErrors(prev => ({ ...prev, product_id: true }));
                        }
                      }}
                      inputProps={{ ...params.inputProps, maxLength: 12 }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Product Details */}
            <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
              Product Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Pack Style *"
                  value={formData.pack_style}
                  onChange={(e) => handleChange('pack_style', e.target.value)}
                  helperText={formData.pack_style === 'PBB' ? '🖨️ Label printing enabled' : ''}
                >
                  {PACK_STYLE_OPTIONS.map((style) => (
                    <MenuItem key={style} value={style}>
                      {style}
                      {style === 'PBB' && ' 🖨️'}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    freeSolo={false}
                    disableClearable
                    options={productsOptions}
                    value={formData.product || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleChange('product', newValue);
                      }
                    }}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Product *"
                        placeholder="Select product..."
                        helperText="Select from list or add new"
                      />
                    )}
                  />
                  <Tooltip title="Add New Product">
                    <IconButton 
                      color="primary" 
                      onClick={() => setShowAddProductDialog(true)}
                      sx={{ mt: 1 }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    freeSolo={false}
                    disableClearable
                    options={gradesOptions}
                    value={formData.grade || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleChange('grade', newValue);
                      }
                    }}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Grade"
                        placeholder="Select grade..."
                        helperText="Select from list or add new"
                      />
                    )}
                  />
                  <Tooltip title="Add New Grade">
                    <IconButton 
                      color="primary" 
                      onClick={() => setShowAddGradeDialog(true)}
                      sx={{ mt: 1 }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    freeSolo={false}
                    disableClearable
                    options={getAvailablePackSizes()}
                    value={formData.pack_size || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleChange('pack_size', newValue);
                      }
                    }}
                    getOptionLabel={(option) => formatPackSizeDisplay(option)}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {formatPackSizeDisplay(option)}
                      </li>
                    )}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Pack Size"
                        placeholder="Select pack size..."
                        helperText={
                          formData.pack_style === 'PBB'
                            ? "✅ PBB: Use 1/10 KG, LOOSE KG, or click + to add custom sizes with decimals (e.g., 30/5.4 OZ, 12.5/2.75 LB)"
                            : "Select from list or click + to add new (decimals supported: 30/5.4 OZ)"
                        }
                      />
                    )}
                  />
                  <Tooltip title={formData.pack_style === 'PBB' ? "Add Custom Pack Size (decimals like 30/5.4 OZ supported)" : "Add New Pack Size (decimals supported)"}>
                    <IconButton 
                      color="primary" 
                      onClick={() => setShowAddPackSizeDialog(true)}
                      sx={{ mt: 1 }}
                    >
                      <AddCircleIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Autocomplete
                    freeSolo={false}
                    disableClearable
                    options={getAvailableBrands()} // ✅ Use filtered brands from NAV ID master
                    value={formData.brand || null}
                    onChange={(e, newValue) => {
                      if (newValue) {
                        handleChange('brand', newValue);
                        setBrandAutoSelected(false); // User manually selected
                      }
                    }}
                    sx={{ 
                      flex: 1,
                      // ✅ Visual indicator when auto-assigned (recommended brand)
                      ...(isBrandLocked() && brandAutoSelected && {
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#e8f5e9',
                          '& fieldset': {
                            borderColor: '#4caf50',
                            borderWidth: 2,
                          },
                        },
                      }),
                      // ✅ Visual indicator when suggested (not from locked list)
                      ...(!isBrandLocked() && brandAutoSelected && {
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#fff3e0',
                          '& fieldset': {
                            borderColor: '#ff9800',
                            borderWidth: 2,
                          },
                        },
                      }),
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={isBrandLocked() ? "Brand ✨ (Auto-Assigned)" : (brandAutoSelected ? "Brand ✨ (Suggested)" : "Brand")}
                        placeholder="Select brand..."
                        helperText={
                          isBrandLocked() 
                            ? "✨ Auto-assigned for this combination - You can change if required" 
                            : brandAutoSelected
                              ? "✨ Suggested from NAV ID master - You can change this"
                              : "Select from list or add new"
                        }
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {brandAutoSelected && (
                                <InputAdornment position="end">
                                  <Tooltip title={isBrandLocked() ? "Auto-assigned brand - you can change if required" : "Suggested brand - you can change this"}>
                                    <AutoAwesomeIcon sx={{ color: isBrandLocked() ? '#4caf50' : '#ff9800', fontSize: 20 }} />
                                  </Tooltip>
                                </InputAdornment>
                              )}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  <Tooltip title="Add New Brand">
                    <span>
                      <IconButton 
                        color="primary" 
                        onClick={() => setShowAddBrandDialog(true)}
                        sx={{ mt: 1 }}
                      >
                        <AddCircleIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={recentTraceIds}
                  value={formData.trace_id}
                  onChange={(e, newValue) => {
                    handleChange('trace_id', newValue || '');
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === 'input') {
                      handleChange('trace_id', newValue || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Recent" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        {option}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={formData.pack_style === 'FINAL'}
                      label={`Farm ID ${formData.pack_style === 'FINAL' ? '*' : ''}`}
                      helperText={
                        recentTraceIds.length > 0
                          ? (formData.pack_style === 'FINAL' ? "Required - Select recent or type new" : "Select recent or type new")
                          : (formData.pack_style === 'FINAL' ? "Required for FINAL pack style" : "")
                      }
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={recentDayCodes}
                  value={formData.day_code}
                  onChange={(e, newValue) => {
                    handleChange('day_code', newValue || '');
                  }}
                  onInputChange={(e, newValue, reason) => {
                    if (reason === 'input') {
                      handleChange('day_code', newValue || '');
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip label="Recent" size="small" color="warning" sx={{ height: 20, fontSize: '0.7rem' }} />
                        {option}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      required={formData.pack_style === 'FINAL'}
                      label={`Day Code ${formData.pack_style === 'FINAL' ? '*' : ''}`}
                      helperText={
                        recentDayCodes.length > 0
                          ? (formData.pack_style === 'FINAL' ? "Required - Select recent or type new" : "Select recent or type new")
                          : (formData.pack_style === 'FINAL' ? "Required for FINAL pack style" : "")
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>

            {/* Pallet Entries */}
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Pallet Entries ({pallets.length})
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addPallet}
              >
                Add Another Pallet
              </Button>
            </Box>
            <Divider sx={{ mb: 3, mt: 1 }} />

            {pallets.map((pallet, index) => (
              <Paper 
                key={pallet.id} 
                sx={{ 
                  p: 2, 
                  mb: 2, 
                  bgcolor: index % 2 === 0 ? '#f5f5f5' : '#fafafa',
                  border: '1px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Pallet #{index + 1}
                  </Typography>
                  <Box>
                    {isPrintLabelEnabled() && (
                      <Tooltip title="Print Labels for this Pallet">
                        <IconButton 
                          color="success" 
                          onClick={() => handleOpenPrintDialog(index)}
                          sx={{ mr: 1 }}
                        >
                          <PrintIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {pallets.length > 1 && (
                      <Tooltip title="Remove this Pallet">
                        <IconButton 
                          color="error" 
                          onClick={() => removePallet(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      required={formData.pack_style === 'FINAL' || formData.pack_style === 'PBB'}
                      label={`Pallet ID ${(formData.pack_style === 'FINAL' || formData.pack_style === 'PBB') ? '*' : ''}`}
                      value={pallet.pallet_id}
                      onChange={(e) => handlePalletChange(index, 'pallet_id', e.target.value)}
                      helperText={(formData.pack_style === 'FINAL' || formData.pack_style === 'PBB') ? "Required for FINAL/PBB" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      required={formData.pack_style === 'FINAL' || formData.pack_style === 'PBB'}
                      label={`Lot No ${(formData.pack_style === 'FINAL' || formData.pack_style === 'PBB') ? '*' : ''}`}
                      value={pallet.lot_no}
                      onChange={(e) => handlePalletChange(index, 'lot_no', e.target.value)}
                      helperText="Default prefix: CTC-"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label={`CS1 (Bin Location) ${(formData.pack_style === 'FINAL' && !isCS1Disabled()) ? '**' : ''}`}
                      value={pallet.cs1}
                      onChange={(e) => handlePalletChange(index, 'cs1', e.target.value)}
                      onBlur={() => handleBlurCS(index, 'cs1')}
                      error={fieldErrors.cs1[index]}
                      helperText={
                        isCS1Disabled() 
                          ? "Disabled for PBB/LOOSE KG" 
                          : formData.pack_style === 'FINAL'
                            ? (fieldErrors.cs1[index] ? "Format: 1B1-1" : "CS1 or CS2 required - e.g., 1B1-1")
                            : (fieldErrors.cs1[index] ? "Format: 1B1-1" : "e.g., 1B1-1")
                      }
                      disabled={isCS1Disabled()}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      required={formData.pack_style === 'PBB'}
                      label={`CS2 (Bin Location) ${formData.pack_style === 'PBB' ? '*' : (formData.pack_style === 'FINAL' ? '**' : '')}`}
                      value={pallet.cs2}
                      onChange={(e) => handlePalletChange(index, 'cs2', e.target.value)}
                      onBlur={() => handleBlurCS(index, 'cs2')}
                      error={fieldErrors.cs2[index]}
                      helperText={
                        formData.pack_style === 'PBB'
                          ? (fieldErrors.cs2[index] ? "Format: 1B1-1 (Required for PBB)" : "Required for PBB - e.g., 1B1-1")
                          : formData.pack_style === 'FINAL'
                            ? (fieldErrors.cs2[index] ? "Format: 1B1-1" : "CS1 or CS2 required - e.g., 1B1-1")
                            : (fieldErrors.cs2[index] ? "Format: 1B1-1" : "e.g., 1B1-1")
                      }
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Cartons"
                      value={pallet.cartons}
                      onChange={(e) => handlePalletChange(index, 'cartons', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                      helperText="Optional (can be 0 for loose only)"
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Loose Bags"
                      value={pallet.loose_bags}
                      onChange={(e) => handlePalletChange(index, 'loose_bags', parseInt(e.target.value) || 0)}
                      inputProps={{ min: 0 }}
                      disabled={isLooseBagsDisabled()}
                      helperText={isLooseBagsDisabled() ? "Disabled for PBB/PTB" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      type="number"
                      label="Loose KGs"
                      value={pallet.loose_kgs}
                      onChange={(e) => handlePalletChange(index, 'loose_kgs', parseFloat(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                      disabled={isKgLooseDisabled()}
                      helperText={isKgLooseDisabled() ? "Disabled for FINAL/FBB" : ""}
                    />
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Paper sx={{ p: 1.5, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Calculated Totals</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {palletsWithCalculatedTotals[index]?.total_kgs?.toFixed(2) || '0.00'} KG
                      </Typography>
                      <Typography variant="body2" color="primary">
                        {palletsWithCalculatedTotals[index]?.total_pounds?.toFixed(2) || '0.00'} LB
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </Paper>
            ))}

            {/* Grand Totals */}
            {pallets.length > 0 && (
              <Paper sx={{ p: 2, bgcolor: '#e8f5e9', mt: 2 }}>
                <Grid container spacing={2} justifyContent="center">
                  <Grid item xs={4} textAlign="center">
                    <Typography variant="body2" color="text.secondary">Total Cartons</Typography>
                    <Typography variant="h6" fontWeight="bold">{grandTotalCartons}</Typography>
                  </Grid>
                  <Grid item xs={4} textAlign="center">
                    <Typography variant="body2" color="text.secondary">Total KGs</Typography>
                    <Typography variant="h6" fontWeight="bold">{grandTotalKgs.toFixed(2)}</Typography>
                  </Grid>
                  <Grid item xs={4} textAlign="center">
                    <Typography variant="body2" color="text.secondary">Total Pounds</Typography>
                    <Typography variant="h6" fontWeight="bold" color="primary">{grandTotalPounds.toFixed(2)}</Typography>
                  </Grid>
                </Grid>
              </Paper>
            )}

            {/* Remarks */}
            <Grid container spacing={2} sx={{ mt: 2 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Remarks"
                  value={formData.remarks}
                  onChange={(e) => handleChange('remarks', e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={loading} startIcon={<CloseIcon />} size="large">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              size="large"
              sx={{
                bgcolor: DAILY_INVENTORY_STATUS_COLORS[formData.status]?.background || 'primary.main',
                '&:hover': {
                  bgcolor: DAILY_INVENTORY_STATUS_COLORS[formData.status]?.borderColor || 'primary.dark',
                }
              }}
            >
              {loading ? 'Saving...' : `Save ${pallets.length > 1 ? `${pallets.length} Entries` : 'Entry'}`}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Label Print Dialog */}
      <LabelPrintDialog
        open={printDialogOpen}
        onClose={() => setPrintDialogOpen(false)}
        labelData={printLabelData}
      />
      
      {/* ✅ Add New Product Dialog */}
      <Dialog 
        open={showAddProductDialog} 
        onClose={() => {
          setShowAddProductDialog(false);
          setNewProductValue('');
          setError('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1565c0', color: 'white' }}>
          ➕ Add New Product
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter product code like: <strong>CPND</strong>, <strong>RPND</strong>, <strong>HLSO</strong>
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="New Product"
            value={newProductValue}
            onChange={(e) => setNewProductValue(e.target.value.toUpperCase())}
            placeholder="e.g., CPND or RPND-NP"
            helperText="Product codes are typically uppercase"
            disabled={addingProduct}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowAddProductDialog(false);
              setNewProductValue('');
              setError('');
            }}
            disabled={addingProduct}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddProduct}
            disabled={addingProduct || !newProductValue.trim()}
            startIcon={addingProduct ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingProduct ? 'Adding...' : 'Add Product'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ Add New Grade Dialog */}
      <Dialog 
        open={showAddGradeDialog} 
        onClose={() => {
          setShowAddGradeDialog(false);
          setNewGradeValue('');
          setError('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#4caf50', color: 'white' }}>
          ➕ Add New Grade
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter grade in format like: <strong>6/8</strong>, <strong>16/20</strong>, <strong>31/40</strong>, or text like <strong>BROKEN</strong>
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="New Grade"
            value={newGradeValue}
            onChange={(e) => {
              // Auto-convert X to /
              const val = e.target.value.replace(/[xX]/g, '/').toUpperCase();
              setNewGradeValue(val);
            }}
            placeholder="e.g., 6/8 or 16/20"
            helperText="Use '/' separator (e.g., 6/8 not 6X8)"
            disabled={addingGrade}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowAddGradeDialog(false);
              setNewGradeValue('');
              setError('');
            }}
            disabled={addingGrade}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="success"
            onClick={handleAddGrade}
            disabled={addingGrade || !newGradeValue.trim()}
            startIcon={addingGrade ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingGrade ? 'Adding...' : 'Add Grade'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ Add New Pack Size Dialog - SUPPORTS DECIMAL VALUES */}
      <Dialog 
        open={showAddPackSizeDialog} 
        onClose={() => {
          setShowAddPackSizeDialog(false);
          setNewPackSizeValue('');
          setError('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1565c0', color: 'white' }}>
          ➕ Add New Pack Size
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>✅ Decimals Supported!</strong>
            <br />
            You can add pack sizes with decimal values like:
            <br />
            • <strong>30/5.4 OZ</strong>
            <br />
            • <strong>12.5/2.75 LB</strong>
            <br />
            • <strong>0.5/2 KG</strong>
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            Standard formats also work: <strong>10/1 LB</strong>, <strong>12/16 OZ</strong>, <strong>1/10 KG</strong>
          </Alert>
          <Alert severity="warning" sx={{ mb: 2 }}>
            ⚠️ <strong>10/32 OZ</strong> is not allowed
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="New Pack Size"
            value={newPackSizeValue}
            onChange={(e) => {
              // Auto-convert X to /
              const val = e.target.value.replace(/[xX]/g, '/').toUpperCase();
              setNewPackSizeValue(val);
            }}
            placeholder="e.g., 30/5.4 OZ, 12.5/2.75 LB, or 10/1 LB"
            helperText="Format: Number/Number UNIT — Decimals allowed in both positions, use '/' not 'X'"
            disabled={addingPackSize}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowAddPackSizeDialog(false);
              setNewPackSizeValue('');
              setError('');
            }}
            disabled={addingPackSize}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="primary"
            onClick={handleAddPackSize}
            disabled={addingPackSize || !newPackSizeValue.trim()}
            startIcon={addingPackSize ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingPackSize ? 'Adding...' : 'Add Pack Size'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* ✅ Add New Brand Dialog */}
      <Dialog 
        open={showAddBrandDialog} 
        onClose={() => {
          setShowAddBrandDialog(false);
          setNewBrandValue('');
          setError('');
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#9c27b0', color: 'white' }}>
          ➕ Add New Brand
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter brand name like: <strong>GREAT CATCH</strong>, <strong>SEAQUEEN</strong>
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="New Brand"
            value={newBrandValue}
            onChange={(e) => setNewBrandValue(e.target.value.toUpperCase())}
            placeholder="e.g., GREAT CATCH"
            helperText="Brand names are stored in uppercase"
            disabled={addingBrand}
          />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setShowAddBrandDialog(false);
              setNewBrandValue('');
              setError('');
            }}
            disabled={addingBrand}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="secondary"
            onClick={handleAddBrand}
            disabled={addingBrand || !newBrandValue.trim()}
            startIcon={addingBrand ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {addingBrand ? 'Adding...' : 'Add Brand'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default AddEntryModal;